// Copyright © 2021 Move Closer

import validate from 'validate.js'

import { RelatedType } from '../../services'

import { AnyObject } from '../_contracts'

/**
 * Abstract class that every module adapter **SHOULD** extend.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export abstract class AbstractAdapter {
  /**
   * Validates the passed-in object against the specified constraints.
   *
   * @param input - The input to validate.
   * @param constraints - Validation instructions (see: https://validatejs.org/#constraints).
   * @param [single=false] - Determines whether the validation will
   * be performed on a single value instead of a complex object.
   *
   * @example - usage:
   *   // Validate complex `object` **OBJECT**.
   *   this.validate(object, {
   *     // Checks if the `foo` key is just present.
   *     foo: { presence: true },
   *
   *     // Checks if the `bar` key is a string.
   *     bar: { type: 'string' },
   *
   *     // Checks if the `baz` key is an array with the length of exactly 3.
   *     baz: {
   *       presence: true,
   *       type: 'array',
   *       length: 3
   *     },
   *
   *     // Checks if the `bar` key nested in the `baz` key has a minimum length of 1.
   *     'hurr.durr': { length: { minimum: 1 } }
   *   })
   *
   *   // Checks if the `id` **VARIABLE** is a string with a minimum length of 1.
   *   this.validate(id, { type: 'string', length: { minimum: 1 } }, true)
   *
   * @see https://validatejs.org/#validate
   */
  protected validate (input: unknown, constraints: AnyObject, single: boolean = false): void {
    const validationResults = single ? validate.single(input, constraints) : validate(input, constraints)

    if (validationResults) {
      this.throwError(validationResults)
    }
  }

  /**
   * Throws the error with the passed-in `validationResults`.
   *
   * @param validationResults - The results of the performed validation.
   */
  private throwError (validationResults: AnyObject): void {
    throw new Error(`${this.constructor.name}: ${JSON.stringify(validationResults)}`)
  }
}

/**
 * @returns - The validate.js-compliant constraint for the `Identifier` type.
 *
 * @param [isRequired=true] - Whether the parameter is required.
 *
 * @see Identifier
 * @see https://validatejs.org/#validate
 */
export const getIdentifierConstraint = (isRequired: boolean = true) => ({
  presence: isRequired,
  type: 'number',
  numericality: {
    onlyInteger: true,
    greaterThan: -2,
    lessThanOrEqualTo: 9223372036854775807
  }
})

/**
 * @returns - The validate.js-compliant constraint for the `Related.type` key.
 *
 * @param type - Whether the parameter is required.
 * @param [isRequired=true] - Whether the parameter is required.
 *
 * @see https://validatejs.org/#validate
 */
export const getRelatedTypeConstraint = (type: RelatedType, isRequired: boolean = true) => ({
  presence: isRequired,
  type: 'string',
  equality: {
    attribute: '-',
    comparator: (_type: RelatedType) => _type === type
  }
})
